import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, UserCog, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Test Login Page
 * 
 * This page handles the automatic login for the Admin Trainee test user.
 * It receives a token via URL parameter, exchanges it for a session, and logs in the user.
 */
const TestLogin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState('authenticating'); // authenticating, success, error
    const [error, setError] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');
        
        if (!token) {
            setStatus('error');
            setError('No authentication token provided');
            return;
        }

        const performLogin = async () => {
            try {
                // Exchange the token for a session (this sets cookies)
                const response = await axios.post(
                    `${API_URL}/api/auth/token-login`,
                    { token },
                    { withCredentials: true }
                );
                
                if (response.data.success) {
                    // Refresh user context to get the new user data
                    await refreshUser();
                    setStatus('success');
                    
                    // Redirect to dashboard after short delay
                    setTimeout(() => {
                        navigate('/dashboard');
                    }, 1500);
                } else {
                    throw new Error('Login failed');
                }
            } catch (err) {
                setStatus('error');
                setError(err.response?.data?.detail || err.message || 'Failed to authenticate');
            }
        };

        performLogin();
    }, [searchParams, navigate, refreshUser]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5">
            <div className="text-center space-y-6 p-8 rounded-2xl bg-card border shadow-xl max-w-md w-full mx-4">
                {/* Icon */}
                <div className={`h-20 w-20 mx-auto rounded-full flex items-center justify-center ${
                    status === 'success' ? 'bg-green-500/10' : 
                    status === 'error' ? 'bg-red-500/10' : 'bg-primary/10'
                }`}>
                    {status === 'authenticating' && (
                        <Loader2 className="h-10 w-10 text-primary animate-spin" />
                    )}
                    {status === 'success' && (
                        <CheckCircle className="h-10 w-10 text-green-500" />
                    )}
                    {status === 'error' && (
                        <UserCog className="h-10 w-10 text-red-500" />
                    )}
                </div>

                {/* Title */}
                <div>
                    <h1 className="text-2xl font-bold">
                        {status === 'authenticating' && 'Logging in as Test User...'}
                        {status === 'success' && 'Welcome, Admin Trainee!'}
                        {status === 'error' && 'Authentication Failed'}
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        {status === 'authenticating' && 'Please wait while we set up your test session'}
                        {status === 'success' && 'Redirecting to your learning dashboard...'}
                        {status === 'error' && error}
                    </p>
                </div>

                {/* Test User Badge */}
                {status === 'success' && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30">
                        <UserCog className="h-4 w-4 text-amber-500" />
                        <span className="text-sm font-medium text-amber-600">Test Mode Active</span>
                    </div>
                )}

                {/* Error Action */}
                {status === 'error' && (
                    <button 
                        onClick={() => window.close()}
                        className="px-6 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
                    >
                        Close Tab
                    </button>
                )}
            </div>
        </div>
    );
};

export default TestLogin;
